/**
 * Component for rendering HTML content safely with proper styling.
 * Used for displaying rich text content from the TipTap editor.
 * Content is sanitized using DOMPurify to prevent XSS attacks.
 */
import { cn } from '../../../lib/utils';
import { sanitizeHtml } from '../../utils/sanitize';

interface HtmlDisplayProps {
  content: string;
  variant?: 'default' | 'compact';
  className?: string;
}

/**
 * Renders HTML content with consistent styling.
 * Use this for displaying rich text content from the editor.
 * All content is sanitized before rendering to prevent XSS.
 */
export function HtmlDisplay({ content, variant = 'default', className }: HtmlDisplayProps) {
  const isCompact = variant === 'compact';
  const sanitizedContent = sanitizeHtml(content);

  return (
    <div
      // `auto` makes the browser pick the base direction from the content's
      // first strong character; per-paragraph `dir` attributes (preserved by
      // sanitizeHtml) override it for individual blocks.
      // bidi-auto-ok: user-authored HTML in an unknown language.
      dir="auto"
      className={cn(
        'prose prose-sm max-w-none dark:prose-invert',
        isCompact && 'prose-p:mb-2 prose-headings:mt-2 prose-headings:mb-1',
        !isCompact && 'prose-p:mb-3 prose-headings:mt-4 prose-headings:mb-2',
        'prose-a:text-primary prose-a:underline',
        'prose-code:rounded prose-code:bg-muted prose-code:px-1.5 prose-code:py-0.5 prose-code:font-mono prose-code:text-[0.875em]',
        'prose-pre:rounded-lg prose-pre:bg-muted prose-pre:p-4',
        'prose-blockquote:border-l-4 prose-blockquote:border-border prose-blockquote:pl-4 prose-blockquote:italic prose-blockquote:text-muted-foreground',
        className
      )}
      // nosemgrep: typescript.react.security.audit.react-dangerouslysetinnerhtml.react-dangerouslysetinnerhtml — sanitizedContent is DOMPurify output (see sanitizeHtml() at line 22)
      dangerouslySetInnerHTML={{ __html: sanitizedContent }}
    />
  );
}
